// Nothing private leaves the browser inside a URL.
//
// The analytics SDKs read the page URL themselves -- outside track()'s privacy
// guard (privacy.ts FORBIDDEN_KEYS): PostHog stamps $current_url / $pathname /
// $referrer ... on every event (autocapture, pageviews, rage clicks, exceptions),
// keys heatmap data by URL and puts the href into replay meta; GA4 sends
// page_location (dl) and page_referrer (dr) on every hit. So every URL is
// rewritten here before they get it:
//
//   - `/local/<file name>` -> `/local/:file`  (a local PDF's name; until 2026-10
//                                               drawing names reached both SDKs)
//   - `/share/<token>`     -> `/share/:token` (a guest share link IS the
//                                               credential -- audit H05)
//   - the fragment is dropped on every route   (`/join#s=<session>&c=<code>`
//                                               carries a pairing code)
//   - query VALUES are kept only for the params in QUERY_KEEP (UTMs and the
//     app's own view state); any other value becomes `:redacted` (a Firebase
//     action link's oobCode / apiKey, a table's free-text `search`, ...).
//
// On a CAPABILITY ROUTE (/share/*, /join -- isCapabilityPath) the analytics do
// not load at all (index.html, utils/analytics.ts, sinks/ga4Sink.ts); this
// redaction is the second line for anything that still names such a URL (a
// same-origin referrer, an SPA navigation after the SDKs loaded).
//
// index.html repeats redactUrl inline (it runs before the bundle);
// urlRedact.test.ts evaluates that snippet against this module.

/** Query params whose VALUES may reach analytics. Every other value -> `:redacted`. */
export const QUERY_KEEP: ReadonlySet<string> = new Set([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'ref',
    'pmtInternal', 'inkdebug', 'agent',
    'view', 'rows', 'cols', 'measure', 'classes', 'sort', 'dir', 'zoom', 'panX', 'panY', 'page',
]);

const REDACTED = ':redacted';

// One path segment after the prefix (stops at /, ?, #, whitespace, quotes and
// PostHog's elements_chain separators). The lookahead keeps the label idempotent.
const LOCAL_SEGMENT = /(\/local\/)(?!:file\b)[^/?#\s"';|]+/g;
const SHARE_SEGMENT = /(\/share\/)(?!:token\b)[^/?#\s"';|]+/g;
// `/join` followed by a query or fragment inside free text (an elements_chain
// href, a replay attribute): drop the tail.
const JOIN_TAIL = /(\/join\/?)[?#][^\s"';|]*/g;
// Credentials that ride in a query, wherever they appear inside free text.
const SECRET_PARAM = /([?&#](?:oobCode|apiKey|token|code|c|s|sig|signature|key|X-Goog-Signature)=)[^&#\s"';|]+/g;

// An absolute http(s) URL or a root-relative path, nothing else.
const URL_LIKE = /^(https?:\/\/[^/?#\s]*)?(\/[^?#\s]*)?(\?[^#\s]*)?(#\S*)?$/i;

/** True for the routes whose URL itself is a credential: `/share/<token>` and `/join`. */
export function isCapabilityPath(pathname: string): boolean {
    return /^\/(share|join)(\/|$)/.test(pathname);
}

/** The current page is a capability route (false outside a browser). */
export function onCapabilityRoute(): boolean {
    return typeof window !== 'undefined' && isCapabilityPath(window.location.pathname);
}

function redactPath(path: string): string {
    return path.replace(LOCAL_SEGMENT, '$1:file').replace(SHARE_SEGMENT, '$1:token');
}

function redactQuery(search: string): string {
    const parts = search.slice(1).split('&').filter(Boolean).map((pair) => {
        const eq = pair.indexOf('=');
        if (eq === -1) return pair;
        const rawKey = pair.slice(0, eq);
        let key = rawKey;
        try { key = decodeURIComponent(rawKey.replace(/\+/g, ' ')); } catch { /* keep raw */ }
        return QUERY_KEEP.has(key) ? pair : `${rawKey}=${REDACTED}`;
    });
    return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Redact free text that may embed URLs (PostHog elements_chain `attr__href="..."`,
 * replay attributes, exception messages): path labels, `/join` tails and the
 * well-known credential params.
 */
export function redactText(value: string): string {
    if (value.indexOf('/') === -1 && value.indexOf('=') === -1) return value;
    return redactPath(value).replace(JOIN_TAIL, '$1').replace(SECRET_PARAM, `$1${REDACTED}`);
}

/**
 * Redact one URL (absolute http(s) or root-relative): path labels, no fragment,
 * query values outside QUERY_KEEP replaced. Anything else is treated as free
 * text (redactText).
 */
export function redactUrl(value: string): string {
    if (!value) return value;
    const m = /^(https?:\/\/|\/)/i.test(value) ? URL_LIKE.exec(value) : null;
    if (!m) return redactText(value);
    return (m[1] ?? '') + redactPath(m[2] ?? '') + redactQuery(m[3] ?? '');
}

/** The redacted current URL, for GA4 page_location. */
export function redactedLocation(): string {
    return typeof window === 'undefined' ? '' : redactUrl(window.location.href);
}

/** The redacted document.referrer, for GA4 page_referrer ('' when none). */
export function redactedReferrer(): string {
    return typeof document === 'undefined' ? '' : redactUrl(document.referrer || '');
}

function redactValue(v: unknown, depth: number): unknown {
    if (typeof v === 'string') return redactUrl(v);
    if (depth <= 0 || v === null || typeof v !== 'object') return v;
    if (Array.isArray(v)) {
        let changed = false;
        const out = v.map((x) => {
            const r = redactValue(x, depth - 1);
            if (r !== x) changed = true;
            return r;
        });
        return changed ? out : v;
    }
    let changed = false;
    // Keys too: PostHog's $heatmap_data is keyed by the page URL.
    const entries = Object.entries(v as Record<string, unknown>).map(([k, x]) => {
        const rk = redactUrl(k);
        const r = redactValue(x, depth - 1);
        if (rk !== k || r !== x) changed = true;
        return [rk, r] as const;
    });
    return changed ? Object.fromEntries(entries) : v;
}

/** The PostHog capture shape this touches (posthog-js CaptureResult). */
export interface CaptureLike {
    event: string;
    properties: Record<string, unknown>;
    $set?: Record<string, unknown>;
    $set_once?: Record<string, unknown>;
}

/**
 * posthog-js `before_send`: redact every string (and key) in the event's
 * properties and person-property bags. Replay batches ($snapshot) are walked
 * deeper -- the rrweb meta event carries the page href.
 */
export function redactCapture<T extends CaptureLike>(cr: T | null): T | null {
    if (!cr) return cr;
    const depth = cr.event === '$snapshot' ? 6 : 3;
    cr.properties = redactValue(cr.properties, depth) as Record<string, unknown>;
    if (cr.$set) cr.$set = redactValue(cr.$set, 2) as Record<string, unknown>;
    if (cr.$set_once) cr.$set_once = redactValue(cr.$set_once, 2) as Record<string, unknown>;
    return cr;
}
